// apps/web/src/components/subscriptions/SubscriptionManagementDialog.tsx
import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import { apiClient } from '../../api/client';
import { SubscriptionType, ActiveSubscriptionInfo } from '@gym-management/shared';
import { AxiosError } from 'axios';

interface SubscriptionManagementDialogProps {
  open: boolean;
  onClose: () => void;
  visitorId?: string;
  currentSubscription?: ActiveSubscriptionInfo;
  onSuccess: () => void;
}

export default function SubscriptionManagementDialog({
  open,
  onClose,
  visitorId,
  currentSubscription,
  onSuccess,
}: SubscriptionManagementDialogProps) {
  const [action, setAction] = useState<'extend' | 'upgrade'>('extend');
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [subscriptionTypes, setSubscriptionTypes] = useState<SubscriptionType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Загружаем типы абонементов при открытии
  useEffect(() => {
    if (open) {
      loadSubscriptionTypes();
      setSelectedTypeId('');
      setError('');
      setAction('extend');
    }
  }, [open]);

  const loadSubscriptionTypes = async () => {
    try {
      const types = await apiClient.getSubscriptionTypes();
      setSubscriptionTypes(types);
    } catch (_err) {
      setError('Не удалось загрузить типы абонементов');
    }
  };

  // Фильтруем тарифы для upgrade: только те, у которых тот же тип (period/visits), что и у текущей подписки
  const availableUpgradeTypes = useMemo(() => {
    if (!currentSubscription) return [];
    return subscriptionTypes.filter(
      (type) =>
        type.type === currentSubscription.subscriptionTypeType &&
        type.id !== currentSubscription.subscriptionTypeId
    );
  }, [subscriptionTypes, currentSubscription]);

  // При смене действия на extend — сбрасываем выбранный тип
  // При смене на upgrade — если есть доступные типы, выбираем первый
  useEffect(() => {
    if (action === 'extend') {
      setSelectedTypeId('');
    } else if (action === 'upgrade') {
      if (availableUpgradeTypes.length > 0) {
        setSelectedTypeId(availableUpgradeTypes[0].id);
      } else {
        setAction('extend');
      }
    }
  }, [action, availableUpgradeTypes]);

  // ========== КОРРЕКТНЫЙ РАСЧЁТ ПРОГНОЗА ==========
  // Остаток дней/посещений уже есть в currentSubscription.subscriptionTypeRemaining
  const remaining = currentSubscription?.subscriptionTypeRemaining ?? 0;

  // Результат продления (extend)
  const getExtendResult = (): string => {
    if (!currentSubscription) return '';
    const { subscriptionTypeType, subscriptionTypeCount } = currentSubscription;

    if (subscriptionTypeType === 'period') {
      const totalDays = remaining + subscriptionTypeCount;
      const newEndDate = new Date();
      newEndDate.setDate(newEndDate.getDate() + totalDays);
      return `✅ Действителен до: ${newEndDate.toLocaleDateString('ru-RU')}`;
    } else {
      const totalVisits = remaining + subscriptionTypeCount;
      return `✅ Доступно посещений: ${totalVisits}`;
    }
  };

  // Результат обновления (upgrade) для выбранного тарифа
  const getUpgradeResult = (typeId: string): string => {
    if (!currentSubscription || !typeId) return '';
    const newType = subscriptionTypes.find((t) => t.id === typeId);
    if (!newType) return '';

    const { subscriptionTypeType: oldType } = currentSubscription;
    const newTypeCategory = newType.type;
    const newCount = newType.count;

    // Остаток в единицах СТАРОГО типа
    const remainingOld = remaining;

    if (newTypeCategory === 'period') {
      // Новый тип period: дни
      let totalDays = newCount;
      if (oldType === 'visits') {
        // Конвертация посещений → дни (1 посещение = 1 день)
        totalDays += remainingOld;
      } else {
        // period → period: переносим оставшиеся дни
        totalDays += remainingOld;
      }
      const newEndDate = new Date();
      newEndDate.setDate(newEndDate.getDate() + totalDays);
      return `✅ Действителен до: ${newEndDate.toLocaleDateString('ru-RU')}`;
    } else {
      // Новый тип visits: посещения
      let totalVisits = newCount;
      if (oldType === 'period') {
        // Конвертация дней → посещения (1 день = 1 посещение)
        totalVisits += remainingOld;
      } else {
        // visits → visits: переносим оставшиеся посещения
        totalVisits += remainingOld;
      }
      return `✅ Доступно посещений: ${totalVisits}`;
    }
  };

  const handleSubmit = async () => {
    if (!visitorId || !currentSubscription) return;
    if (action === 'upgrade' && !selectedTypeId) return;

    setLoading(true);
    setError('');

    try {
      await apiClient.renewSubscription({
        visitorId,
        newSubscriptionTypeId:
          action === 'upgrade' ? selectedTypeId : currentSubscription.subscriptionTypeId,
        action,
      });
      onSuccess();
      onClose();
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        setError(err.response?.data?.message || err.message || 'Ошибка при обработке запроса');
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Ошибка при обработке запроса');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!currentSubscription) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Управление абонементом</DialogTitle>
      <DialogContent>
        {/* Информация о текущем абонементе */}
        <Alert severity="info" sx={{ mb: 2 }} icon={<FitnessCenterIcon />}>
          <Typography variant="subtitle2" component="div">
            {currentSubscription.subscriptionTypeName}
          </Typography>
          {currentSubscription.endDate && (
            <Typography variant="caption" display="block">
              Действует до: {new Date(currentSubscription.endDate).toLocaleDateString('ru-RU')}
            </Typography>
          )}
          <Typography variant="caption" display="block">
            {currentSubscription.subscriptionTypeType === 'period'
              ? `Осталось дней: ${remaining}`
              : `Осталось посещений: ${remaining}`}
          </Typography>
        </Alert>

        {/* Выбор действия */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Действие</FormLabel>
          <RadioGroup
            row
            value={action}
            onChange={(e) => setAction(e.target.value as 'extend' | 'upgrade')}
          >
            <FormControlLabel value="extend" control={<Radio />} label="Продлить" />
            <FormControlLabel
              value="upgrade"
              control={<Radio />}
              label="Обновить тариф"
              disabled={availableUpgradeTypes.length === 0}
            />
          </RadioGroup>
        </FormControl>

        {/* Блок для обновления тарифа */}
        {action === 'upgrade' && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Выберите новый тариф (только{' '}
              {currentSubscription.subscriptionTypeType === 'period' ? 'срочные' : 'по посещениям'}
              ):
            </Typography>
            <FormControl fullWidth disabled={loading} sx={{ mb: 2 }}>
              <InputLabel>Новый тариф</InputLabel>
              <Select
                value={selectedTypeId}
                onChange={(e) => setSelectedTypeId(e.target.value)}
                label="Новый тариф"
              >
                {availableUpgradeTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name} (
                    {type.type === 'period' ? `${type.count} дней` : `${type.count} посещений`})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* ===== БЛОК ПРОГНОЗА ===== */}
        {action === 'extend' && (
          <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarTodayIcon color="primary" fontSize="small" />
              <Typography variant="subtitle2" color="primary">
                Результат продления
              </Typography>
            </Box>
            <Typography variant="body2">{getExtendResult()}</Typography>
          </Paper>
        )}

        {action === 'upgrade' && selectedTypeId && (
          <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarTodayIcon color="secondary" fontSize="small" />
              <Typography variant="subtitle2" color="secondary">
                Результат обновления
              </Typography>
            </Box>
            <Typography variant="body2">{getUpgradeResult(selectedTypeId)}</Typography>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Отмена
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (action === 'upgrade' && !selectedTypeId)}
        >
          {loading ? <CircularProgress size={24} /> : action === 'extend' ? 'Продлить' : 'Обновить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
